<c-container class="dashboard-container" fluid>

  <!-- ═══════════════════════════════════════════════════════════════════════
       "Health in the Light" (customer decisions 2026-09-20/22). Order is the
       customer's: 1 greeting · 2 Dr. Future · 3 Health Club entries ·
       4 membership · 5 Community Points · 6 next appointments · 7 the rest.
       ═══════════════════════════════════════════════════════════════════════ -->

  <!-- 1 · Welcome: the wolf sits at the right edge, looking INTO the content. -->
  <section class="welcome" aria-labelledby="welcome-h">
    <h1 id="welcome-h">
      @if (firstName()) {
        {{ 'home.welcome_back' | translate: { name: firstName() } }}
      } @else {
        {{ 'home.welcome_generic' | translate }}
      }
    </h1>
    <p class="welcome__q">{{ 'home.question' | translate }}</p>
    <div class="welcome__badges">
      <!-- Founding Member / Founding Practitioner: historic honours with their own
           business logic (WP-E); the badges render nothing for everyone else. -->
      <founding-member-badge [foundingMember]="user()?.founding_member"
                             [memberNumber]="user()?.founding_member_number" />
      <founding-member-badge kind="practitioner"
                             [foundingMember]="user()?.founding_practitioner"
                             [memberNumber]="user()?.founding_practitioner_number" />
    </div>
    <!-- The claim is brand language and deliberately not translated. -->
    <p class="welcome__claim">YOUR LIFE. YOUR CHOICE.<span>{{ 'home.claim_sub' | translate }}</span></p>
    <svg class="welcome__horizon" viewBox="0 0 900 160" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 120 C 150 60, 300 140, 450 100 S 750 40, 900 90 L 900 160 L 0 160 Z" fill="currentColor"/>
    </svg>
    <!-- Wolf silhouette removed (customer, 2026-10-04). The right-hand side stays empty on purpose:
         the customer will place the "WolfPack Signature Element" here later. -->
  </section>

  <!-- Account created by the direct checkout (no registration before the purchase):
       the e-mail is confirmed afterwards, so remind instead of blocking. -->
  @if (showVerifyBanner()) {
    <div class="alert alert-warning d-flex flex-column flex-md-row align-items-md-center gap-2 verify-banner" role="status">
      <div class="flex-grow-1">
        <strong>{{ 'home.verify_email.title' | translate }}</strong>
        <div class="small">
          @switch (verifyMailState()) {
            @case ('sent') { {{ 'home.verify_email.sent' | translate }} }
            @case ('already_confirmed') { {{ 'home.verify_email.already_confirmed' | translate }} }
            @case ('error') { {{ 'home.verify_email.error' | translate }} }
            @default { {{ 'home.verify_email.body' | translate:{ email: user()?.email ?? '' } }} }
          }
        </div>
      </div>
      <div class="d-flex gap-2">
        <button cButton color="primary" size="sm" type="button"
                [disabled]="verifyMailState() === 'sending' || verifyMailState() === 'sent'"
                (click)="resendVerifyMail()">
          {{ 'home.verify_email.resend' | translate }}
        </button>
        <button cButton color="secondary" variant="ghost" size="sm" type="button" (click)="dismissVerifyBanner()">
          {{ 'home.verify_email.dismiss' | translate }}
        </button>
      </div>
    </div>
  }

  <!-- 1b · Meine Begleitung: the daily companion of the DNA & Health SPECIAL, right
       under the greeting (Walter, 2026-11-04) — the member's first daily action.
       Only with health_companion_enabled (explicit boolean from the backend);
       the card shows the entitlement state, the page explains the rest. -->
  @if (user()?.health_companion_enabled === true && user()?.health_companion; as hc) {
    <section class="card companion-block" aria-labelledby="companion-h">
      <div class="companion-block__head">
        <h3 id="companion-h">{{ 'home.companion.title' | translate }}</h3>
        <a [routerLink]="['/', currentLang(), 'companion']">{{ 'home.companion.open' | translate }}</a>
      </div>
      <p class="companion-block__hint">
        @switch (hc.status) {
          @case ('active') { {{ 'home.companion.active' | translate:{ days: hc.days_left } }} }
          @case ('minor_consent_required') { {{ 'home.companion.minor' | translate }} }
          @case ('expired') { {{ 'home.companion.expired' | translate }} }
          @default { {{ 'home.companion.hint' | translate }} }
        }
      </p>
      <a cButton color="primary" size="sm" [routerLink]="['/', currentLang(), 'companion']">{{ 'home.companion.checkin' | translate }}</a>
    </section>
  }

  <!-- 2 · Dr. Future: the one thing that explains the membership in three seconds. -->
  <section class="card hero" aria-labelledby="hero-h">
    <div class="hero__main">
      <div class="hero__kicker">{{ 'home.hero.kicker' | translate }}</div>
      <h2 id="hero-h">{{ 'home.hero.title' | translate }}</h2>
      <p class="hero__sub">{{ 'home.hero.subtitle' | translate }}</p>
      <form class="hero__ask" (ngSubmit)="askDrFuture()">
        <input type="text" name="q" [ngModel]="heroQuestion()" (ngModelChange)="heroQuestion.set($event)"
               [placeholder]="'home.hero.placeholder' | translate" [attr.aria-label]="'home.hero.placeholder' | translate">
        <button cButton color="primary" type="submit">
          {{ 'home.hero.cta' | translate }} <svg cIcon name="cilArrowRight" size="sm"></svg>
        </button>
      </form>
      <p class="hero__note">{{ 'home.hero.note' | translate }}</p>
    </div>
  </section>

  <!-- 3 · The Health Club entries -->
  <div class="section-title"><h3>{{ 'home.section_club' | translate }}</h3></div>
  <div class="hcards">
    @for (card of healthCards; track card.key) {
      @if (card.enabled && card.route) {
        <a class="hcard" [routerLink]="['/', currentLang(), ...card.route]">
          <span class="hcard__icon"><svg cIcon [name]="card.icon" size="lg"></svg></span>
          <h4>{{ ('home.cards.' + card.key + '.title') | translate }}</h4>
          <p>{{ ('home.cards.' + card.key + '.text') | translate }}</p>
          <span class="hcard__go">{{ ('home.cards.' + card.key + '.go') | translate }} <svg cIcon name="cilArrowRight" size="sm"></svg></span>
        </a>
      } @else {
        <!-- Coming soon (customer answers 5/6): clearly a future function, never a dead link. -->
        <div class="hcard hcard--soon" aria-disabled="true">
          <span class="hcard__pill">{{ 'home.coming_soon' | translate }}</span>
          <span class="hcard__icon"><svg cIcon [name]="card.icon" size="lg"></svg></span>
          <h4>{{ ('home.cards.' + card.key + '.title') | translate }}</h4>
          <p>{{ ('home.cards.' + card.key + '.text') | translate }}</p>
          <span class="hcard__go hcard__go--muted">{{ ('home.cards.' + card.key + '.soon') | translate }}</span>
        </div>
      }
    }
  </div>

  <!-- 4 · Membership: plan picker before a purchase, one card per slot after. -->
  <div class="section-title"><h3>{{ 'home.membership_title' | translate }}</h3></div>
  <!-- No membership yet: offer all five plans. Only once one is selected does the
       dashboard split into the two per-category slot cards below. -->
  @if (!hasMembership()) {
    <c-row class="mb-4">
      <c-col xs="12">
        <h3 class="mb-1">{{ 'membership.choose.title' | translate }}</h3>
        <p class="text-body-secondary mb-0">{{ 'membership.choose.subtitle' | translate }}</p>
      </c-col>
    </c-row>

    <!-- One card per PRODUCT; its durations are options chosen in the wizard,
         never products of their own (customer decision 2026-10-02). -->
    <div class="plan-picker mb-4">
      @for (group of planTierGroups(); track group.tier) {
        <c-card class="plan-card">
          <c-card-body class="d-flex flex-column">
            @if (group.plans[0].photo_url) {
              <img class="plan-card__img" [src]="group.plans[0].photo_url" [alt]="('gh_product.' + group.product + '.name') | translate" />
            }
            <h4 class="plan-card__name">{{ ('gh_product.' + group.product + '.name') | translate }}</h4>
            <p class="plan-card__tagline">{{ ('gh_product.' + group.product + '.tagline') | translate }}</p>
            @if (group.tier === 'practitioner') {
              <p class="founding-note mb-2">{{ 'membership.tier.founding_note' | translate }}</p>
            }

            <ul class="plan-card__features">
              @for (feature of planFeatures(group.plans[0].code); track feature) {
                <li>{{ feature }}</li>
              }
            </ul>

            @for (p of group.plans; track p.code) {
              @if (paymentTestMode) {
                <div class="plan-card__price-note">${{ p.price | number:'1.0-2' }} · {{ 'membership.plan.test_price_note' | translate }}</div>
              } @else {
                <div class="plan-card__price-note">{{ planField(p.code, 'price_year') }}</div>
              }
            }

            <button cButton color="primary" class="plan-card__cta mt-auto" (click)="orderProduct(group)">
              {{ 'membership.plan.select' | translate }}
            </button>
          </c-card-body>
        </c-card>
      }

      <!-- DNA & Health SPECIAL (customer instruction 2026-10-19): open to a new
           customer too. Shown only when the backend lists the product as on sale;
           the price is the server's. -->
      @if (specialPlan(); as sp) {
        <c-card class="plan-card">
          <c-card-body class="d-flex flex-column">
            @if (sp.photo_url) {
              <img class="plan-card__img" [src]="sp.photo_url" [alt]="'gh_product.dna_health_special.name' | translate" />
            }
            <h4 class="plan-card__name">{{ 'gh_product.dna_health_special.name' | translate }}</h4>
            <p class="plan-card__tagline">{{ 'membership.special.dashboard_body' | translate }}</p>
            <ul class="plan-card__features">
              @for (part of specialComponents; track part) {
                <li>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</li>
              }
              <li>{{ 'gh_product.dna_health_special.guarantee' | translate }}</li>
            </ul>
            <div class="plan-card__price-note">
              {{ specialPrice() }}@if (paymentTestMode) { · {{ 'membership.plan.test_price_note' | translate }} }
            </div>
            <button cButton color="primary" class="plan-card__cta mt-auto" (click)="startSpecialPurchase()">
              {{ 'membership.special.dashboard_cta' | translate:{ price: specialPrice() } }}
            </button>
          </c-card-body>
        </c-card>
      }
    </div>
  }

  <!-- Membership slots: one card per category (protection + health club). Shown
       only once the customer holds/started a membership in at least one slot. -->
  @if (hasMembership()) {
  @for (slot of slots(); track slot.category) {
    @switch (slotState(slot.app)) {

      <!-- Unfinished application — finalize / complete bank transfer -->
      @case ('pending') {
        <c-row class="mb-4">
          <c-col xs="12">
            <c-card class="dashboard-card membership-reminder">
              <c-card-body class="d-flex flex-column flex-md-row align-items-md-center gap-3">
                <div class="card-icon membership-reminder__icon">
                  <svg cIcon name="cilShieldCheck" size="lg"></svg>
                </div>
                @if (pendingBankTransferFor(slot.app); as po) {
                  <div class="flex-grow-1">
                    <div class="text-body-secondary small text-uppercase mb-1">{{ ('membership.slot.' + slot.category) | translate }}</div>
                    <h3 cCardTitle class="mb-1">{{ 'membership.dashboard_widget.bank_transfer_title' | translate }}</h3>
                    <p cCardText class="mb-1">{{ 'membership.dashboard_widget.bank_transfer_body' | translate }}</p>
                    @if (po.bank_transfer_reference) {
                      <p cCardText class="mb-0">
                        <strong>{{ 'membership.dashboard_widget.bank_transfer_reference' | translate }}:</strong>
                        {{ po.bank_transfer_reference }}
                      </p>
                    }
                  </div>
                  <button cButton color="primary" size="lg" class="membership-reminder__cta" (click)="resumeMembership(slot.app)">
                    {{ 'membership.dashboard_widget.bank_transfer_cta' | translate }}
                  </button>
                } @else {
                  <div class="flex-grow-1">
                    <div class="text-body-secondary small text-uppercase mb-1">{{ ('membership.slot.' + slot.category) | translate }}</div>
                    <h3 cCardTitle class="mb-1">{{ 'membership.dashboard_widget.title' | translate }}</h3>
                    <p cCardText class="mb-0">{{ 'membership.dashboard_widget.body' | translate }}</p>
                  </div>
                  <button cButton color="primary" size="lg" class="membership-reminder__cta" (click)="resumeMembership(slot.app)">
                    {{ 'membership.dashboard_widget.cta' | translate }}
                  </button>
                }
              </c-card-body>
            </c-card>
          </c-col>
        </c-row>
      }

      <!-- Coverage ran out and nobody renewed. The slot still belongs to the member:
           renewing revives this membership, it does not create a second one. -->
      @case ('expired') {
        <c-row class="mb-4">
          <c-col xs="12">
            <c-card class="dashboard-card">
              <c-card-body>
                <div class="mb-2">
                  <span class="badge bg-secondary me-2">{{ ('membership.slot.' + slot.category) | translate }}</span>
                  <strong>{{ planName(slot.app) }}</strong>
                  <span class="badge bg-warning text-dark ms-2">{{ 'membership.status.expired_badge' | translate }}</span>
                </div>
                <p class="mb-2">{{ 'membership.status.expired' | translate }}</p>
                @if (slot.app!.coverage_end_date) {
                  <div class="text-body-secondary small mb-3">
                    {{ 'membership.status.coverage_end' | translate }}: {{ formatDate(slot.app!.coverage_end_date) }}
                  </div>
                }
                <!-- No plan is carried over: coverage has ended, so all four
                     offered plans are open again and the member picks freely. -->
                <button cButton color="primary" (click)="renewWithoutPlan(slot.app)">
                  {{ renewCtaKey(slot.app) | translate }}
                </button>
              </c-card-body>
            </c-card>
          </c-col>
        </c-row>
      }

      <!-- Post-purchase membership status (Step 15) -->
      @case ('purchased') {
        <c-row class="mb-4">
          <c-col xs="12">
            <c-card class="dashboard-card">
              <c-card-body>
                <div class="d-flex align-items-center gap-2 mb-2">
                  <svg cIcon name="cilShieldCheck" size="lg"></svg>
                  <h3 cCardTitle class="mb-0">{{ 'membership.status.title' | translate }}</h3>
                </div>

                <div class="mb-2">
                  <span class="badge bg-secondary me-2">{{ ('membership.slot.' + slot.category) | translate }}</span>
                  <strong>{{ planName(slot.app) }}</strong>
                </div>

                @if (slot.app!.membership_status === 'ACTIVE') {
                  <p class="mb-2">{{ 'membership.status.active' | translate }}</p>
                } @else {
                  <p class="mb-2">{{ 'membership.status.pending' | translate }}</p>
                }

                <!-- DNA & Health bundle (Website V2): what the bundle still owes —
                     kit, nutrition plan, and for the SPECIAL the satisfaction
                     guarantee with its claim window. -->
                @if (bundleOf(slot.app); as bundle) {
                  <div class="membership-bundle mb-2">
                    @if (bundle.product_code === 'GH_DNA_FAMILY_SPECIAL') {
                    <!-- DNA Family SPECIAL: same components, no guarantee — and it says so. -->
                    <div class="small fw-semibold mb-1">{{ 'gh_product.dna_family_special.name' | translate }}</div>
                    <ul class="small mb-2 ps-3">
                      @for (part of ['dna', 'nutrition', 'fitness', 'dr_future', 'membership']; track part) {
                        <li>
                          <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                          — {{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}
                        </li>
                      }
                      <li>{{ 'gh_product.dna_family_special.no_guarantee' | translate }}</li>
                    </ul>
                    }
                    @if (bundle.product_code === 'GH_DNA_HEALTH_SPECIAL') {
                    <div class="small fw-semibold mb-1">{{ 'gh_product.dna_health_special.name' | translate }}</div>
                    <ul class="small mb-2 ps-3">
                      @for (part of ['dna', 'nutrition', 'fitness', 'dr_future', 'membership']; track part) {
                        <li>
                          <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                          — {{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}
                        </li>
                      }
                      @if (bundle.guarantee_amount) {
                        <li><strong>{{ 'membership.bundle.guarantee_amount' | translate:{ amount: offerAmount(bundle.guarantee_amount, 'USD') } }}</strong></li>
                      }
                    </ul>
                    }
                    <!-- Start area (customer briefing 2026-11-02): the shipping address
                         of the DNA kit is the member's FIRST task after the purchase
                         (§3, worldwide — the country is part of the address), then
                         the course in plain words and the Practitioner team contact (§4). -->
                    @if (bundle.shipping_address_required && shippingFormFor() !== slot.app!.id) {
                      <div class="alert alert-warning py-2 px-3 small mb-2">
                        <strong>{{ 'membership.bundle.shipping.task_title' | translate }}</strong><br />
                        {{ 'membership.bundle.shipping.task_text' | translate }}
                        <div class="mt-2">
                          <button cButton color="primary" size="sm" (click)="openShippingForm(slot.app)">
                            {{ 'membership.bundle.shipping.enter' | translate }}
                          </button>
                        </div>
                      </div>
                    }
                    @if (shippingFormFor() === slot.app!.id) {
                      <div class="membership-shipping-form mb-2 p-2 border rounded">
                        <div class="small fw-semibold mb-1">{{ 'membership.bundle.shipping.form_title' | translate }}</div>
                        <div class="small text-body-secondary mb-2">{{ 'membership.bundle.shipping.form_hint' | translate }}</div>
                        <div class="row g-2">
                          <div class="col-12">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.name' | translate }} *</label>
                            <input class="form-control form-control-sm" [class.is-invalid]="shippingFieldInvalid('shipping_name')"
                                   maxlength="200" autocomplete="name" [(ngModel)]="shipping.shipping_name" />
                          </div>
                          <div class="col-12">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.address' | translate }} *</label>
                            <input class="form-control form-control-sm" [class.is-invalid]="shippingFieldInvalid('shipping_address')"
                                   maxlength="255" autocomplete="address-line1" [(ngModel)]="shipping.shipping_address" />
                          </div>
                          <div class="col-12">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.address2' | translate }}</label>
                            <input class="form-control form-control-sm" maxlength="255" autocomplete="address-line2"
                                   [(ngModel)]="shipping.shipping_address2" />
                          </div>
                          <div class="col-4">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.postcode' | translate }}</label>
                            <input class="form-control form-control-sm" [class.is-invalid]="shippingFieldInvalid('shipping_postcode')"
                                   maxlength="32" autocomplete="postal-code" [(ngModel)]="shipping.shipping_postcode" />
                          </div>
                          <div class="col-8">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.city' | translate }} *</label>
                            <input class="form-control form-control-sm" [class.is-invalid]="shippingFieldInvalid('shipping_city')"
                                   maxlength="120" autocomplete="address-level2" [(ngModel)]="shipping.shipping_city" />
                          </div>
                          <div class="col-12 col-md-6">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.state' | translate }}</label>
                            <input class="form-control form-control-sm" maxlength="120" autocomplete="address-level1"
                                   [(ngModel)]="shipping.shipping_state" />
                          </div>
                          <div class="col-12 col-md-6">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.country' | translate }} *</label>
                            <select class="form-select form-select-sm" [class.is-invalid]="shippingFieldInvalid('shipping_country')"
                                    autocomplete="country" [(ngModel)]="shipping.shipping_country">
                              <option value="">—</option>
                              @for (c of countries(); track c.code) {
                                <option [value]="c.code">{{ c.name }}</option>
                              }
                            </select>
                          </div>
                          <div class="col-12">
                            <label class="form-label small mb-0">{{ 'membership.bundle.shipping.fields.phone' | translate }}</label>
                            <input class="form-control form-control-sm" maxlength="40" autocomplete="tel"
                                   [(ngModel)]="shipping.shipping_phone" />
                            <div class="form-text">{{ 'membership.bundle.shipping.fields.phone_hint' | translate }}</div>
                          </div>
                        </div>
                        @if (shippingError(); as error) {
                          <div class="small text-danger mt-2">{{ error }}</div>
                        }
                        <div class="d-flex gap-2 mt-2">
                          <button cButton color="primary" size="sm" [disabled]="!canSubmitShipping()" (click)="submitShippingAddress(slot.app)">
                            {{ 'membership.bundle.shipping.save' | translate }}
                          </button>
                          <button cButton color="secondary" variant="ghost" size="sm" (click)="cancelShippingForm()">
                            {{ 'membership.bundle.shipping.cancel' | translate }}
                          </button>
                        </div>
                      </div>
                    }
                    @if (bundle.shipping_address?.complete && shippingFormFor() !== slot.app!.id) {
                      <div class="small mb-2">
                        <span class="fw-semibold">{{ 'membership.bundle.shipping.stored' | translate }}</span>
                        <address class="mb-1">
                          @for (line of shippingAddressLines(bundle); track $index) {
                            {{ line }}<br />
                          }
                        </address>
                        @if (bundle.shipping_address_editable) {
                          <button cButton color="link" size="sm" class="p-0" (click)="openShippingForm(slot.app)">
                            {{ 'membership.bundle.shipping.change' | translate }}
                          </button>
                        } @else {
                          <span class="text-body-secondary">{{ 'membership.bundle.shipping.locked' | translate }}</span>
                        }
                      </div>
                    }
                    <!-- Kit, nutrition plan and fitness plan are three separate
                         deliverables, shown separately (customer decision F). -->
                    <ul class="small list-unstyled mb-0">
                      <li>
                        {{ 'membership.bundle.kit' | translate }}: <strong>{{ bundleKitLabel(bundle) }}</strong>
                        @if (bundle.tracking_number) {
                          · {{ 'membership.bundle.tracking' | translate }}: {{ bundle.tracking_number }}
                        }
                      </li>
                      <li>{{ 'membership.bundle.nutrition_plan' | translate }}: <strong>{{ bundlePlanLabel(bundle.nutrition_plan_status) }}</strong></li>
                      <li>{{ 'membership.bundle.fitness_plan' | translate }}: <strong>{{ bundlePlanLabel(bundle.fitness_plan_status) }}</strong></li>
                      @if (bundleGoalLabel(bundle); as goal) {
                        <li>{{ 'membership.bundle.your_goal' | translate }}: <strong>{{ goal }}</strong></li>
                      }
                    </ul>
                    <!-- The course in plain words (derived from the statuses) and the
                         Practitioner team contact. -->
                    <ol class="membership-bundle-steps small mt-2 mb-2 ps-3">
                      @for (step of bundleSteps(bundle); track step.key) {
                        <li [class.text-body-secondary]="step.state === 'upcoming'" [class.fw-semibold]="step.state === 'current'">
                          {{ ('membership.bundle.steps.' + step.key) | translate }}
                          @if (step.state === 'done') { <span class="text-success">✓</span> }
                          @if (step.state === 'current') { <span class="badge bg-warning text-dark ms-1">{{ 'membership.bundle.steps.now' | translate }}</span> }
                          @if (step.key === 'release' && step.date && step.state !== 'done') {
                            <span class="text-body-secondary">({{ formatDate(step.date) }})</span>
                          }
                        </li>
                      }
                    </ol>
                    @if (contactEmail(); as email) {
                      <div class="small mb-2">
                        {{ 'membership.bundle.contact' | translate }}
                        <a href="mailto:{{ email }}">{{ email }}</a>
                      </div>
                    }
                    <!-- The guarantee only when this bundle carries one, with ITS amount
                         (decision E of 2026-10-03). -->
                    @if (bundle.guarantee_deadline && bundle.guarantee_amount) {
                      <div class="small mt-1">
                        @if (bundle.guarantee_claimed_at) {
                          {{ 'membership.guarantee.submitted' | translate:{ date: formatDate(bundle.guarantee_claimed_at) } }}
                          @if (bundle.guarantee_payout_method) {
                            <br />{{ 'membership.guarantee.payout.submitted_method' | translate:{
                                       method: guaranteePayoutLabel(bundle.guarantee_payout_method) } }}
                          }
                          @if (bundle.guarantee_paid_at) {
                            <br />{{ 'membership.guarantee.payout.paid' | translate:{ date: formatDate(bundle.guarantee_paid_at) } }}
                          } @else {
                            <br />{{ 'membership.guarantee.payout.pending' | translate:{ days: bundle.guarantee_payout_max_days ?? 30 } }}
                          }
                        } @else {
                          <!-- Six weeks from the deadline; open-ended only when the
                               backend runs GH_GUARANTEE_CLAIM_WINDOW_DAYS=0. -->
                          @if (bundle.guarantee_window_end) {
                            {{ 'membership.guarantee.window' | translate:{
                                 from: formatDate(bundle.guarantee_deadline),
                                 until: formatDate(bundle.guarantee_window_end) } }}
                          } @else {
                            {{ 'membership.guarantee.window_open' | translate:{ from: formatDate(bundle.guarantee_deadline) } }}
                          }
                          <br /><span class="text-body-secondary">{{ 'membership.guarantee.terms' | translate }}</span>
                        }
                      </div>
                      @if (bundle.guarantee_claim_open && guaranteeFormFor() !== slot.app!.id) {
                        <button cButton color="primary" variant="outline" size="sm" class="mt-2"
                                [disabled]="guaranteeBusy()" (click)="openGuaranteeForm(slot.app)">
                          {{ 'membership.guarantee.claim' | translate }}
                        </button>
                      }
                      @if (bundle.guarantee_claim_open && guaranteeFormFor() === slot.app!.id) {
                        <!-- The claim form: how the guarantee is paid out, where to, and
                             an explicit confirmation (the claim cannot be undone here). -->
                        <div class="membership-guarantee-form mt-2 p-2 border rounded">
                          <div class="small fw-semibold mb-1">{{ 'membership.guarantee.payout.title' | translate }}</div>
                          <div class="small text-body-secondary mb-2">{{ 'membership.guarantee.terms' | translate }}</div>
                          @for (method of ['crypto', 'bank_transfer']; track method) {
                            <label class="d-block small mb-1">
                              <input type="radio" name="guarantee-payout-{{ slot.app!.id }}" [value]="method"
                                     [checked]="guaranteeMethod() === method"
                                     (change)="guaranteeMethod.set($any(method)); guaranteeError.set(null)" />
                              <strong>{{ ('membership.guarantee.payout.' + method + '.label') | translate }}</strong>
                              — {{ ('membership.guarantee.payout.' + method + '.explain') | translate }}
                            </label>
                          }
                          @if (guaranteeMethod(); as method) {
                            <label class="d-block small mt-2">
                              {{ ('membership.guarantee.payout.' + method + '.details_label') | translate }}
                              <textarea class="form-control form-control-sm mt-1" rows="3" maxlength="1000"
                                        [value]="guaranteeDetails()"
                                        (input)="guaranteeDetails.set($any($event.target).value)"></textarea>
                            </label>
                            <div class="text-body-secondary small">
                              {{ ('membership.guarantee.payout.' + method + '.details_hint') | translate }}
                            </div>
                            @if (guaranteeDetails().trim() && !guaranteeDetailsValid()) {
                              <div class="text-danger small">
                                {{ (method === 'crypto' ? 'membership.guarantee.payout.error.payout_wallet_invalid'
                                                        : 'membership.guarantee.payout.error.payout_details_too_long') | translate }}
                              </div>
                            }
                            <label class="d-block small mt-2">
                              <input type="checkbox" [checked]="guaranteeConfirmed()"
                                     (change)="guaranteeConfirmed.set($any($event.target).checked)" />
                              {{ 'membership.guarantee.payout.confirm' | translate }}
                            </label>
                          }
                          <div class="mt-2 d-flex gap-2">
                            <button cButton color="primary" size="sm" [disabled]="!canSubmitGuarantee()"
                                    (click)="claimGuarantee(slot.app)">
                              {{ 'membership.guarantee.payout.submit' | translate }}
                            </button>
                            <button cButton color="secondary" variant="ghost" size="sm" [disabled]="guaranteeBusy()"
                                    (click)="closeGuaranteeForm()">
                              {{ 'membership.guarantee.payout.cancel' | translate }}
                            </button>
                          </div>
                        </div>
                      }
                      @if (guaranteeError()) {
                        <div class="text-danger small mt-1">{{ guaranteeError() }}</div>
                      }
                    }
                  </div>
                }

                <div class="row g-3 mb-2">
                  @if (slot.app!.coverage_start_date) {
                    <div class="col-sm-6">
                      <div class="text-body-secondary small">{{ 'membership.status.coverage_start' | translate }}</div>
                      <div>{{ formatDate(slot.app!.coverage_start_date) }}</div>
                    </div>
                  }
                  @if (slot.app!.coverage_end_date) {
                    <div class="col-sm-6">
                      <div class="text-body-secondary small">{{ 'membership.status.coverage_end' | translate }}</div>
                      <div>{{ formatDate(slot.app!.coverage_end_date) }}</div>
                    </div>
                  }
                  @if (slot.app!.cancellation_deadline) {
                    <div class="col-sm-6">
                      <div class="text-body-secondary small">{{ 'membership.status.cancellation_deadline' | translate }}</div>
                      <div>{{ formatDate(slot.app!.cancellation_deadline) }}</div>
                    </div>
                  }
                </div>

                <!-- Renewing early costs nothing: the new term is added to the
                     current end date, so no paid coverage is lost. -->
                <!-- Special conditions of a MANUAL booking the member has to confirm
                     (customer decision 7 of 2026-10-04: guarantee none / custom, or a
                     discounted SPECIAL). A notice with an accept button — it never
                     blocks the membership. -->
                @for (bt of openBookingTerms(slot.app); track bt.booking_id) {
                  <div class="alert alert-warning mb-2" role="status">
                    <div class="fw-semibold mb-1">{{ 'membership.booking_terms.title' | translate }}</div>
                    <ul class="mb-2 ps-3">
                      @for (line of bookingTermsLines(bt); track $index) { <li>{{ line }}</li> }
                    </ul>
                    <p class="small mb-2">{{ 'membership.booking_terms.confirm_note' | translate }}</p>
                    <button cButton color="primary" size="sm" [disabled]="termsBusy() === bt.booking_id"
                            (click)="acknowledgeBookingTerms(bt)">
                      {{ 'membership.booking_terms.accept' | translate }}
                    </button>
                    @if (termsError() && termsBusy() === null) { <div class="small text-danger mt-1">{{ termsError() }}</div> }
                  </div>
                }

                @if (slot.app!.renewable) {
                  <button cButton color="primary" variant="outline" size="sm" class="mb-2"
                          (click)="renewMembership(slot.app)">
                    {{ renewCtaKey(slot.app) | translate }}
                  </button>
                }

                <!-- Practitioner Network upgrade: only rendered when the server
                     issued an offer for this membership (paid, running, still on
                     the club tier). Price and package are the offer's. -->
                @if (upgradeOfferFor(slot.app); as offer) {
                  <div class="membership-upgrade mb-2">
                    <div class="fw-semibold mb-1">{{ 'membership.upgrade.dashboard_title' | translate }}</div>
                    @for (paragraph of upgradeParagraphs(); track $index) {
                      <p class="mb-2">{{ paragraph }}</p>
                    }
                    <button cButton color="primary" size="sm" (click)="startUpgrade(slot.app)">
                      {{ 'membership.upgrade.dashboard_cta' | translate:{ price: upgradePrice(offer) } }}
                    </button>
                  </div>
                }

                <!-- Upgrade to the DNA & Health SPECIAL (customer decision 2026-10-03, D):
                     only when the server issued an offer — ACTIVE, self-paid, yearly. -->
                @if (specialUpgradeOfferFor(slot.app); as su) {
                  <div class="membership-upgrade mb-2">
                    <div class="fw-semibold mb-1">{{ 'membership.special_upgrade.dashboard_title' | translate }}</div>
                    <p class="mb-2">{{ 'membership.special_upgrade.dashboard_body' | translate:{ credit: offerAmount(su.credit, su.currency) } }}</p>
                    <button cButton color="primary" size="sm" (click)="startSpecialUpgrade()">
                      {{ 'membership.special_upgrade.dashboard_cta' | translate:{ price: offerAmount(su.total_price, su.currency) } }}
                    </button>
                  </div>
                }

                <!-- The REGULAR DNA & Health SPECIAL (customer instruction 2026-10-19):
                     for every membership the server offers it to (offered_bundle_codes),
                     Practitioners included, eligible for the upgrade or not. With an
                     upgrade offer above, this is the second, regular way — both stay
                     visible, the upgrade first. Price = the server's plan price. -->
                @if (specialPurchaseAvailable(slot.app)) {
                  <div class="membership-upgrade mb-2">
                    <div class="fw-semibold mb-1">{{ 'membership.special.dashboard_title' | translate }}</div>
                    <p class="mb-2">{{ 'membership.special.dashboard_body' | translate }}</p>
                    <ul class="small mb-2 ps-3">
                      @for (part of specialComponents; track part) {
                        <li>
                          <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                          — {{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}
                        </li>
                      }
                    </ul>
                    <p class="small mb-1"><strong>{{ 'gh_product.dna_health_special.guarantee' | translate }}</strong></p>
                    <p class="small mb-1">{{ 'membership.special.includes_membership' | translate }}</p>
                    @if (isPractitionerTier(slot.app)) {
                      <p class="small mb-1">{{ 'membership.special.practitioner_note' | translate }}</p>
                    }
                    @if (specialUpgradeOfferFor(slot.app)) {
                      <button cButton color="primary" variant="outline" size="sm" class="mt-1" (click)="startSpecialPurchase()">
                        {{ 'membership.special.or_regular' | translate:{ price: specialPrice() } }}
                      </button>
                    } @else {
                      <button cButton color="primary" size="sm" class="mt-1" (click)="startSpecialPurchase()">
                        {{ 'membership.special.dashboard_cta' | translate:{ price: specialPrice() } }}
                      </button>
                    }
                  </div>
                }

                <!-- Practitioner Membership on a running DNA & Health SPECIAL (customer
                     decisions of 2026-10-04): offered to EVERY SPECIAL member, since the
                     SPECIAL includes the regular membership but not the Practitioner one.
                     It runs on its own date — the SPECIAL's own term is untouched. The
                     text follows the member's history: still a Practitioner (active),
                     a Practitioner whose term ended (ended), or never one (none —
                     retained_until is null). -->
                @if (slot.app!.practitioner_renewal_offer; as pr) {
                  <div class="membership-upgrade mb-2">
                    <div class="fw-semibold mb-1">{{ 'membership.practitioner_renewal.dashboard_title' | translate }}</div>
                    <p class="mb-2">
                      @if (pr.retained && pr.retained_until) {
                        {{ 'membership.practitioner_renewal.dashboard_body_active' | translate:{ date: formatDate(pr.retained_until) } }}
                      } @else if (pr.retained_until) {
                        {{ 'membership.practitioner_renewal.dashboard_body_ended' | translate:{ date: formatDate(pr.retained_until) } }}
                      } @else {
                        {{ 'membership.practitioner_renewal.dashboard_body_none' | translate }}
                      }
                    </p>
                    <button cButton color="primary" size="sm" (click)="startPractitionerRenewal()">
                      {{ 'membership.practitioner_renewal.dashboard_cta' | translate }}
                    </button>
                  </div>
                }

                <!-- The full-price way in, for a membership that was never paid for
                     (admin-fee conversion, gift) or was paid on a retired rhythm:
                     there is no difference to buy, so the term is bought at the
                     regular price and ADDED on top of the current end date. The two
                     routes never show together — the server decides which applies. -->
                @if (practitionerJoinAvailable(slot.app)) {
                  <div class="membership-upgrade mb-2">
                    <div class="fw-semibold mb-1">{{ 'membership.join_practitioner.dashboard_title' | translate }}</div>
                    @for (paragraph of practitionerJoinParagraphs(slot.app); track $index) {
                      <p class="mb-2">{{ paragraph }}</p>
                    }
                    <button cButton color="primary" size="sm" (click)="startPractitionerJoin(slot.app)">
                      {{ 'membership.join_practitioner.dashboard_cta' | translate }}
                    </button>
                  </div>
                }

                @if (slot.app!.documents?.length) {
                  <div class="text-body-secondary small mb-1">{{ 'membership.status.documents' | translate }}</div>
                  <div class="d-flex flex-wrap gap-2 mb-2">
                    @for (d of slot.app!.documents; track d.doc_type) {
                      <button cButton color="primary" variant="outline" size="sm" (click)="viewMembershipDocument(d.doc_type, slot.category)">
                        {{ documentLabel(d.doc_type) }}
                      </button>
                    }
                  </div>
                }

                @if (slot.app!.can_cancel) {
                  <button cButton color="danger" variant="outline" size="sm" (click)="cancelMembership(slot.app)">
                    {{ 'membership.status.cancel' | translate }}
                  </button>
                }
              </c-card-body>
            </c-card>
          </c-col>
        </c-row>
      }

      <!-- Empty slot — offer to add/buy this membership -->
      @default {
        <!-- One card per thing this slot sells. The club slot sells two tiers, so it
             offers both side by side instead of hiding the Practitioner Network
             behind the plan step. -->
        @for (variant of buyWidgetVariants(slot); track variant) {
          <c-row class="mb-4">
            <c-col xs="12">
              <c-card class="dashboard-card membership-reminder">
                <c-card-body class="d-flex flex-column flex-md-row align-items-md-center gap-3">
                  <div class="card-icon membership-reminder__icon">
                    <svg cIcon name="cilShieldCheck" size="lg"></svg>
                  </div>
                  <div class="flex-grow-1">
                    <div class="text-body-secondary small text-uppercase mb-1">{{ ('membership.slot.' + slot.category) | translate }}</div>
                    <h3 cCardTitle class="mb-1">{{ ('membership.buy_widget.' + variant + '_title') | translate }}</h3>
                    <p cCardText class="mb-0">{{ buyWidgetBodyKey(slot, variant) | translate }}</p>
                    @if (variant === 'practitioner') {
                      <p class="founding-note mb-0 mt-1">{{ 'membership.tier.founding_note' | translate }}</p>
                    }
                  </div>
                  <button cButton color="primary" size="lg" class="membership-reminder__cta"
                          (click)="startBuyWidget(slot.category, variant)">
                    {{ ('membership.buy_widget.' + variant + '_cta') | translate:{ price: specialPrice() } }}
                  </button>
                </c-card-body>
              </c-card>
            </c-col>
          </c-row>
        }
      }
    }
  }
  }

  <!-- 4b · My Family (DNA Family SPECIAL): only for a qualified SPECIAL buyer —
       the backend sends family_enabled explicitly; anything but true hides it. -->
  @if (user()?.family_enabled === true && user()?.family; as fam) {
    <section class="card family-block" aria-labelledby="family-h">
      <div class="family-block__head">
        <h3 id="family-h">{{ 'home.family.title' | translate }}</h3>
        <a [routerLink]="['/', currentLang(), 'family']">{{ 'home.family.manage' | translate }}</a>
      </div>
      <p class="family-block__hint">{{ 'home.family.hint' | translate }}</p>
      <div class="family-block__counters">
        <div><b>{{ fam.used }}</b><span>{{ 'home.family.used' | translate:{ slots: fam.slots } }}</span></div>
        <div><b>{{ fam.open }}</b><span>{{ 'home.family.open' | translate }}</span></div>
        <div><b>{{ fam.purchased }}</b><span>{{ 'home.family.purchased' | translate }}</span></div>
        <div><b>{{ fam.remaining }}</b><span>{{ 'home.family.remaining' | translate }}</span></div>
      </div>
      @if (fam.remaining > 0) {
        <a cButton color="primary" size="sm" [routerLink]="['/', currentLang(), 'family']" [queryParams]="{ invite: 1 }">
          {{ 'home.family.invite' | translate }}
        </a>
      }
    </section>
  }

  <!-- 4c · Marketing Pool: only for holders of investments or referrers with
       referrals — the card fetches nothing for anyone else and renders nothing
       without investments or referrals. -->
  <app-marketing-pool-card />

  <!-- 5 · Community Points, compact — the same widget as in distribution. -->
  <div class="wpcp-compact">
    <app-wpcp-dashboard-widget />
  </div>

  <!-- 5b · My referral links (Website V2): one copyable link per website journey and
       bundle, carrying ?ref=<id>. The dashboard had no referral element of its own,
       so this sits with the partner widget above. -->
  @if (referralLinks().length) {
    <section class="card reflinks" aria-labelledby="reflinks-h">
      <h3 id="reflinks-h">{{ 'home.referral_links.title' | translate }}</h3>
      <p class="reflinks__hint">{{ 'home.referral_links.hint' | translate }}</p>
      @for (link of referralLinks(); track link.key) {
        <div class="reflink">
          <div class="reflink__text">
            <div class="reflink__name">{{ 'home.referral_links.' + link.key | translate }}</div>
            <div class="reflink__url">{{ link.url }}</div>
          </div>
          <button cButton type="button" size="sm" [color]="copiedReferral() === link.key ? 'success' : 'primary'"
                  variant="outline" (click)="copyReferral(link)">
            {{ (copiedReferral() === link.key ? 'home.referral_links.copied' : 'home.referral_links.copy') | translate }}
          </button>
        </div>
      }
    </section>
  }

  <!-- 6 · Next appointments -->
  @if (upcoming().length) {
    <section class="card appts" aria-labelledby="appts-h">
      <div class="appts__head">
        <h3 id="appts-h">{{ 'home.appointments_title' | translate }}</h3>
        <a [routerLink]="['/', currentLang(), 'appointments']">{{ 'home.appointments_all' | translate }}</a>
      </div>
      @for (a of upcoming(); track a.occurrence_id) {
        <div class="appt">
          <div class="appt__date"><b>{{ appointmentDay(a) }}</b><span>{{ appointmentMonth(a) }}</span></div>
          <div>
            <div class="appt__title">{{ a.title }}</div>
            <div class="appt__meta">{{ appointmentWhen(a) }} · {{ a.time_zone }}</div>
          </div>
        </div>
      }
    </section>
  }

  <!-- 7 · Newsletter opt-in: only rendered for users who unsubscribed; deliberately last. -->
  <div class="quiet">
    <app-newsletter-optin></app-newsletter-optin>
  </div>
</c-container>
